import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Search, MoreVertical, Calendar, Phone } from "lucide-react";
import { toast } from "sonner";
import { GetUsersParams, userService } from "@/services/user";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DateUtils } from "@/utils/dateUtils";
import { User, UsersResponse } from "@/types/user.types";

// 类型
type MemberStatusUI = "all" | "yes" | "no";
type QueryField = "user_id" | "nickname" | "phone" | "invite_code";
type TimeFilterType = "register_time" | "vip_expire_time";

type Member = {
  id: string;
  avatar?: string | null;
  phone?: string | null;
  nickname?: string | null;
  register_time: string;
  vip_expire_time?: string | null;
  invite_code?: string | null;
  invite_count: number;
  level: number;
  lexile?: number | null;
  memberStatus: 0|1|2|99;
};
const memberStatusType = {
  0: "否",
  1: "普通",
  2: "VIP",
  99: "已过期"
}

export default function MemberManagement() {
  // 分页
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // 数据
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);

  // 1) 条件查询：字段 + 输入
  const [field, setField] = useState<QueryField>("user_id");
  const [keyword, setKeyword] = useState("");

  // 2) 时间筛选
  const [timeType, setTimeType] = useState<TimeFilterType>("register_time");
  const [timeStart, setTimeStart] = useState<string>("");
  const [timeEnd, setTimeEnd] = useState<string>("");

  // 3) 用户级别范围
  const [levelStart, setLevelStart] = useState<string>("none");
  const [levelEnd, setLevelEnd] = useState<string>("none");

  // 4) 会员状态（UI）映射为接口的 -1/0/1
  const [vipStatusUI, setVipStatusUI] = useState<MemberStatusUI>("all");

  // 5) 蓝思值范围
  const [lexileStart, setLexileStart] = useState<string>("");
  const [lexileEnd, setLexileEnd] = useState<string>("");

  // 6) 邀请人数范围
  const [inviteMin, setInviteMin] = useState<string>("");
  const [inviteMax, setInviteMax] = useState<string>("");

  // 变更手机号弹窗相关状态
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [processingPhone, setProcessingPhone] = useState(false);

  // 设置会员时间弹窗相关状态
  const [membershipDialogOpen, setMembershipDialogOpen] = useState(false);
  const [newExpiryDate, setNewExpiryDate] = useState("");
  const [processingMembership, setProcessingMembership] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  // UI状态 -> 接口参数映射
  const buildRequestParams = () => {
    const params: GetUsersParams = {
      page,
      size: pageSize, // 注意这里使用page_size而不是size
    };

    // 条件查询四选一
    const kw = keyword.trim();
    if (kw) {
      if (field === "user_id") params.user_id = kw;
      if (field === "nickname") params.nick_name = kw; // 后端期望nick_name
      if (field === "phone") params.phone = kw;
      if (field === "invite_code") params.invitation_code = kw; // 后端期望invitation_code
    }

    // 时间筛选：根据 timeType 映射
    if (timeStart || timeEnd) {
      if (timeType === "register_time") {
        if (timeStart)
          params.reg_start_time = DateUtils.toChineseISOString(
            timeStart,
            false
          );
        if (timeEnd)
          params.reg_end_time = DateUtils.toChineseISOString(timeEnd, true);
      } else if (timeType === "vip_expire_time") {
        if (timeStart)
          params.invitation_end_start_time = DateUtils.toChineseISOString(
            timeStart,
            false
          );
        if (timeEnd)
          params.invitation_end_end_time = DateUtils.toChineseISOString(
            timeEnd,
            true
          );
      }
    }

    // 级别
    if (levelStart !== "none") params.start_level = Number(levelStart);
    if (levelEnd !== "none") params.end_level = Number(levelEnd);

    // 会员状态
    // -1 不限；0 否；1 是
    params.member_status =
      vipStatusUI === "all" ? -1 : vipStatusUI === "yes" ? 1 : 0;

    // 蓝思值
    if (lexileStart !== "") params.start_blue_score = Number(lexileStart) * 100;
    if (lexileEnd !== "") params.end_blue_score = Number(lexileEnd) * 100;

    // 邀请人数范围
    if (inviteMin !== "") params.start_invitation_cnt = Number(inviteMin);
    if (inviteMax !== "") params.end_invitation_cnt = Number(inviteMax);

    return params;
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const res: UsersResponse = await userService.getUsers(params);

      // 更新映射逻辑以匹配API返回的结构
      const list = res.list ?? [];
      const totalCount = res.total ?? 0;
      const memberList = list.map((user: User) => ({
        id: user.id,
        avatar: user.avatar,
        phone: user.phone,
        nickname: user.name, // API返回的是name字段
        register_time: user.createdAt, // API返回的是createdAt
        vip_expire_time: user.memberEndDate, // API返回的是memberEndDate
        memberStatus: user.memberStatus, // 1表示是会员
        invite_code: user.invitationCode, // API返回的是invitationCode
        invite_count: user.invitationNum || user.invitationMemberCnt || 0, // 使用invitationNum或invitationMemberCnt
        level: user.levelNum, // API返回的是levelNum
        lexile: (user.blueScore as number) / 100, // API返回的是blueScore
      }));
      setMembers(memberList);
      setSortedMembers(memberList);
      setTotal(totalCount);
    } catch (e) {
      console.error(e);
      toast.error("获取会员列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchMembers();
  };

  const resetFilters = () => {
    setField("user_id");
    setKeyword("");
    setTimeType("register_time");
    setTimeStart("");
    setTimeEnd("");
    setLevelStart("none");
    setLevelEnd("none");
    setVipStatusUI("all");
    setLexileStart("");
    setLexileEnd("");
    setInviteMin("");
    setInviteMax("");
    setPage(1);
  };

  // 处理变更手机号弹窗打开
  const handlePhoneDialog = (member: Member) => {
    setSelectedMember(member);
    setNewPhoneNumber("");
    setPhoneDialogOpen(true);
  };

  // 处理设置会员时间弹窗打开
  const handleMembershipDialog = (member: Member) => {
    setSelectedMember(member);
    if (member.vip_expire_time) {
      // 如果有现有的到期时间，截取日期部分作为默认值
      const dateOnly = member.vip_expire_time.split("T")[0].split(" ")[0];
      setNewExpiryDate(dateOnly);
    } else {
      // 如果没有，默认设置为今天日期一年后
      const today = new Date();
      today.setFullYear(today.getFullYear() + 1);
      setNewExpiryDate(today.toISOString().split("T")[0]);
    }
    setMembershipDialogOpen(true);
  };

  // 处理更新手机号
  const handleUpdatePhone = async () => {
    if (!selectedMember || !newPhoneNumber) return;

    // 简单验证手机号格式
    if (!/^1[3-9]\d{9}$/.test(newPhoneNumber)) {
      toast.error("手机号格式不正确", {
        description: "请输入正确的11位手机号码",
      });
      return;
    }

    setProcessingPhone(true);
    try {
      // 这里调用实际的API
      await userService.updateUserPhone(selectedMember.id, newPhoneNumber);

      toast.success("手机号更新成功", {
        description: `用户 ${
          selectedMember.nickname || selectedMember.id
        } 的手机号已更新`,
      });

      // 关闭弹窗并刷新数据
      setPhoneDialogOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error("更新手机号失败", {
        description: error instanceof Error ? error.message : "未知错误",
      });
    } finally {
      setProcessingPhone(false);
    }
  };

  // 处理更新会员时间
  const handleUpdateMembership = async () => {
    if (!selectedMember || !newExpiryDate) return;

    setProcessingMembership(true);
    try {
      // 将日期字符串转换为ISO 8601格式，带有中国时区
      const formattedDate = DateUtils.toChineseISOString(newExpiryDate, true);

      // 这里调用实际的API
      await userService.updateUserMembership(selectedMember.id, formattedDate);

      toast.success("会员时间更新成功", {
        description: `用户 ${
          selectedMember.nickname || selectedMember.id
        } 的会员到期时间已更新为 ${new Date(
          formattedDate
        ).toLocaleDateString()}`,
      });

      // 关闭弹窗并刷新数据
      setMembershipDialogOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error("更新会员时间失败", {
        description: error instanceof Error ? error.message : "未知错误",
      });
    } finally {
      setProcessingMembership(false);
    }
  };

  const levelOptions = Array.from({ length: 16 }, (_, i) => i + 1);

  // 排序后的用户
  const [sortedMembers, setSortedMembers] = useState<Member[]>([]);
  const onSortStateChange = (newMembers: Member[]) => {
    setSortedMembers(newMembers);
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">会员管理</h2>
      </div>

      {/* 筛选区 */}
      <div className="rounded-lg border p-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* 1. 条件查询 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">条件查询</label>
            <div className="flex gap-2">
              <Select
                value={field}
                onValueChange={(v) => setField(v as QueryField)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="选择字段" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user_id">用户ID</SelectItem>
                  <SelectItem value="nickname">用户昵称</SelectItem>
                  <SelectItem value="phone">用户手机号</SelectItem>
                  <SelectItem value="invite_code">邀请码</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="请输入查询内容"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1"
              />
            </div>
          </div>

          {/* 2. 时间筛选（注册/到期） */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">时间筛选</label>
            <div className="flex gap-2">
              <Select
                value={timeType}
                onValueChange={(v) => setTimeType(v as TimeFilterType)}
              >
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="选择时间类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="register_time">注册时间</SelectItem>
                  <SelectItem value="vip_expire_time">会员到期时间</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={timeStart}
                onChange={(e) => setTimeStart(e.target.value)}
              />
              <Input
                type="date"
                value={timeEnd}
                onChange={(e) => setTimeEnd(e.target.value)}
              />
            </div>
          </div>

          {/* 3. 级别范围 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">
              用户级别范围（L1-L16）
            </label>
            <div className="flex gap-2">
              <Select
                value={levelStart}
                onValueChange={(v) => setLevelStart(v)}
              >
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="开始(L)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">不限</SelectItem>
                  {levelOptions.map((lv) => (
                    <SelectItem key={lv} value={String(lv)}>
                      L{lv}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={levelEnd} onValueChange={(v) => setLevelEnd(v)}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="结束(L)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">不限</SelectItem>
                  {levelOptions.map((lv) => (
                    <SelectItem key={lv} value={String(lv)}>
                      L{lv}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* 第二行 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
          {/* 4. 会员状态 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">会员状态</label>
            <div className="flex gap-2">
              <Select
                value={vipStatusUI}
                onValueChange={(v) => setVipStatusUI(v as MemberStatusUI)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="选择状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">不限</SelectItem>
                  <SelectItem value="yes">是</SelectItem>
                  <SelectItem value="no">否</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 5. 蓝思值范围 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">蓝思值范围</label>
            <div className="flex gap-2">
              <Input
                type="text"
                inputMode="numeric"
                placeholder="开始"
                value={lexileStart}
                onChange={(e) => {
                  const value = e.target.value;
                  // 只允许负号开头和数字
                  if (value === "" || value === "-" || /^-?\d+$/.test(value)) {
                    setLexileStart(value);
                  }
                }}
              />
              <Input
                type="text"
                inputMode="numeric"
                placeholder="结束"
                value={lexileEnd}
                onChange={(e) => {
                  const value = e.target.value;
                  // 只允许负号开头和数字
                  if (value === "" || value === "-" || /^-?\d+$/.test(value)) {
                    setLexileEnd(value);
                  }
                }}
              />
            </div>
          </div>

          {/* 6. 邀请人数范围 + 操作按钮 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">邀请人数范围</label>
            <div className="flex gap-2">
              <Input
                inputMode="numeric"
                placeholder="最小"
                value={inviteMin}
                onChange={(e) =>
                  setInviteMin(e.target.value.replace(/[^\d]/g, ""))
                }
              />
              <Input
                inputMode="numeric"
                placeholder="最大"
                value={inviteMax}
                onChange={(e) =>
                  setInviteMax(e.target.value.replace(/[^\d]/g, ""))
                }
              />
              <div className="ml-auto flex gap-2">
                <Button variant="outline" onClick={resetFilters}>
                  重置
                </Button>
                <Button onClick={handleSearch}>
                  <Search className="mr-2 h-4 w-4" />
                  查询
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 列表：可滚动容器 */}
      <div className="rounded-md border overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">用户ID</TableHead>
              <TableHead className="whitespace-nowrap">用户头像</TableHead>
              <TableHead className="whitespace-nowrap">用户手机号</TableHead>
              <TableHead className="whitespace-nowrap">用户昵称</TableHead>
              <TableHead className="whitespace-nowrap">
                {/* <span>注册时间</span> */}
                <SortIcon
                  text="注册时间"
                  resources={members}
                  // total={total}
                  // pageSize={pageSize}
                  // page={page}
                  onSortStateChange={onSortStateChange}
                  sortItem="register_time"
                />
              </TableHead>
              <TableHead className="whitespace-nowrap">会员到期时间</TableHead>
              <TableHead className="whitespace-nowrap">会员状态</TableHead>
              <TableHead className="whitespace-nowrap">邀请码</TableHead>
              <TableHead className="whitespace-nowrap">邀请人数</TableHead>
              <TableHead className="whitespace-nowrap">级别</TableHead>
              <TableHead className="whitespace-nowrap">蓝思值</TableHead>
              <TableHead className="whitespace-nowrap text-right">
                操作
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="h-24 text-center">
                  没有找到会员
                </TableCell>
              </TableRow>
            ) : (
              sortedMembers.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="whitespace-nowrap">{m.id}</TableCell>
                  <TableCell>
                    {m.avatar ? (
                      <img
                        src={`${import.meta.env.VITE_OSS_HOST}${m.avatar}`}
                        alt={String(m.id)}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600">
                        N/A
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {m.phone || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {m.nickname || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {m.register_time
                      ? new Date(m.register_time).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {m.vip_expire_time
                      ? new Date(m.vip_expire_time).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {memberStatusType[m.memberStatus] || "否"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {m.invite_code || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {m.invite_count}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {m.level ? `L${m.level}` : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {m.lexile ?? "-"}
                  </TableCell>

                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-muted/60 rounded-full"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        sideOffset={4}
                        className="w-40 rounded-xl shadow-lg border bg-white"
                      >
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          相关操作
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                          onClick={() => handleMembershipDialog(m)}
                          className="cursor-pointer"
                        >
                          <Calendar className="mr-2 h-4 w-4 text-emerald-600" />
                          设置会员时间
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handlePhoneDialog(m)}
                          className="cursor-pointer"
                        >
                          <Phone className="mr-2 h-4 w-4 text-indigo-600" />
                          变更手机号
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!loading && members.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 whitespace-nowrap">
            共 {total} 位会员，第 {page} 页 / 共 {totalPages} 页
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => page > 1 && setPage(page - 1)}
                  className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <PaginationItem key={i}>
                    <PaginationLink
                      isActive={page === pageNum}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => page < totalPages && setPage(page + 1)}
                  className={
                    page >= totalPages ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* 变更手机号弹窗 */}
      <Dialog
        open={phoneDialogOpen}
        onOpenChange={(open) => !open && setPhoneDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>变更手机号</DialogTitle>
            <DialogDescription>修改用户的关联手机号码</DialogDescription>
          </DialogHeader>

          {selectedMember && (
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">用户:</span>
                  <span className="font-medium">
                    {selectedMember.nickname || "未命名用户"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">用户ID:</span>
                  <span className="font-mono">{selectedMember.id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">当前手机号:</span>
                  <span className="font-medium">
                    {selectedMember.phone || "未设置"}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-phone">新手机号</Label>
                <Input
                  id="new-phone"
                  type="tel"
                  placeholder="请输入11位手机号码"
                  value={newPhoneNumber}
                  onChange={(e) => setNewPhoneNumber(e.target.value)}
                  pattern="^1[3-9]\d{9}$"
                />
                <p className="text-xs text-gray-500">请输入新的11位手机号码</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
              onClick={handleUpdatePhone}
              disabled={
                processingPhone ||
                !newPhoneNumber ||
                newPhoneNumber.length !== 11
              }
            >
              {processingPhone ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                  处理中...
                </>
              ) : (
                "确认更新"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 设置会员时间弹窗 */}
      <Dialog
        open={membershipDialogOpen}
        onOpenChange={(open) => !open && setMembershipDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>设置会员到期时间</DialogTitle>
            <DialogDescription>修改用户的会员服务到期日期</DialogDescription>
          </DialogHeader>

          {selectedMember && (
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">用户:</span>
                  <span className="font-medium">
                    {selectedMember.nickname || "未命名用户"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">手机号:</span>
                  <span>{selectedMember.phone || "未设置"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">当前到期时间:</span>
                  <span
                    className={
                      selectedMember.vip_expire_time
                        ? "text-green-600 font-medium"
                        : "text-gray-500"
                    }
                  >
                    {selectedMember.vip_expire_time
                      ? new Date(
                          selectedMember.vip_expire_time
                        ).toLocaleDateString()
                      : "未设置会员"}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiry-date">新的到期日期</Label>
                <Input
                  id="expiry-date"
                  type="date"
                  value={newExpiryDate}
                  onChange={(e) => setNewExpiryDate(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  选择新的会员到期日期，系统将自动设置为当天的23:59:59
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
              onClick={handleUpdateMembership}
              disabled={processingMembership || !newExpiryDate}
            >
              {processingMembership ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                  处理中...
                </>
              ) : (
                "确认更新"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
